import React, { createContext, useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { auth } from '../firebase';
import { 
  onAuthStateChanged, 
  getIdToken,
  getIdTokenResult,
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail
} from 'firebase/auth';

const AuthContext = createContext({});
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login'); 
  const refreshTimerRef = useRef(null);

  // --- Utility: Error Messaging ---
  const getFirebaseErrorMessage = useCallback((error) => {
    switch (error.code) {
      case 'auth/invalid-email': return 'Invalid email address.';
      case 'auth/user-disabled': return 'This account has been disabled.';
      case 'auth/user-not-found': return 'No account found with this email.';
      case 'auth/wrong-password': return 'Incorrect password.';
      case 'auth/email-already-in-use': return 'An account already exists with this email.';
      case 'auth/weak-password': return 'Password is too weak.';
      case 'auth/network-request-failed': return 'Network error. Please check your connection.';
      default: return error.message || 'Authentication failed. Please try again.';
    }
  }, []);

  // --- Logic: Password Reset ---
  const resetPassword = useCallback(async (email) => {
    try {
      await firebaseSendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('[Auth] Password reset error:', error);
      return { success: false, error: getFirebaseErrorMessage(error) };
    }
  }, [getFirebaseErrorMessage]);

  // --- Logic: Email Verification ---
  const checkEmailVerified = useCallback(async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload();
      const updatedUser = auth.currentUser;
      setUser(prev => prev ? ({ ...prev, emailVerified: updatedUser.emailVerified }) : null);
      return updatedUser.emailVerified;
    }
    return false;
  }, []);

  const sendVerificationEmail = useCallback(async (email) => {
    try {
      const currentToken = token || (auth.currentUser ? await getIdToken(auth.currentUser) : null);
      if (!currentToken) throw new Error('No authentication token available');

      const response = await fetch('/api/auth/send-verification', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed with status: ${response.status}`);
      }
      return { success: true };
    } catch (error) {
      console.error('[Auth] Verification email error:', error);
      return { success: false, error: error.message };
    }
  }, [token]);

  // --- Logic: Token Management ---
  const refreshToken = useCallback(async (firebaseUser) => {
    try {
      const freshToken = await getIdToken(firebaseUser, true);
      const tokenResult = await getIdTokenResult(firebaseUser);
      setToken(freshToken);
      sessionStorage.setItem('auth_token', freshToken);
      sessionStorage.setItem('token_expiry', tokenResult.expirationTime);
      return freshToken;
    } catch (error) {
      console.error('[Auth] Token refresh failed:', error);
      return null;
    }
  }, []);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const isTokenValid = useCallback(() => {
    const expiry = sessionStorage.getItem('token_expiry');
    if (!expiry) return false;
    const expiryTime = new Date(expiry).getTime();
    const currentTime = new Date().getTime();
    return currentTime < (expiryTime - 5 * 60 * 1000); // 5 min buffer
  }, []);

  const forceTokenRefresh = useCallback(async () => {
    if (auth.currentUser) return await refreshToken(auth.currentUser);
    return null;
  }, [refreshToken]);

  // --- Logic: Lifecycle (Auth State Observer) ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      clearRefreshTimer();

      if (firebaseUser) {
        try {
          const initialToken = await getIdToken(firebaseUser);
          const tokenResult = await getIdTokenResult(firebaseUser);
          
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            emailVerified: firebaseUser.emailVerified,
          });
          setToken(initialToken);
          
          sessionStorage.setItem('auth_token', initialToken);
          sessionStorage.setItem('token_expiry', tokenResult.expirationTime);
          localStorage.setItem('user', JSON.stringify({ uid: firebaseUser.uid, email: firebaseUser.email }));

          refreshTimerRef.current = setInterval(() => refreshToken(firebaseUser), 55 * 60 * 1000);
        } catch (error) {
          console.error('[Auth] Initialization error:', error);
          setUser(null);
          setToken(null);
          sessionStorage.clear();
        }
      } else {
        setUser(null);
        setToken(null);
        sessionStorage.clear();
        localStorage.removeItem('user');
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      clearRefreshTimer();
    };
  }, [clearRefreshTimer, refreshToken]);

  // --- Logic: Auth Actions ---
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (error) {
      return { success: false, error: getFirebaseErrorMessage(error) };
    } finally {
      setLoading(false);
    }
  }, [getFirebaseErrorMessage]);

  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
      return { success: true, user: userCredential.user };
    } catch (error) {
      return { success: false, error: getFirebaseErrorMessage(error) };
    } finally {
      setLoading(false);
    }
  }, [getFirebaseErrorMessage]);

  const logout = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      sessionStorage.clear();
      localStorage.clear();
    } catch (error) {
      console.error('[Auth] Logout error:', error);
    }
  }, []);

  // --- Modal Control ---
  const openAuthModal = useCallback((mode = 'login') => {
    setAuthModalMode(mode);
    setIsAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => setIsAuthModalOpen(false), []);

  // --- The Value Memo (CRITICAL FIX) ---
  const isAuthenticated = useMemo(() => !!user && !!token && isTokenValid(), [user, token, isTokenValid]);

  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated,
    logout,
    forceTokenRefresh,
    isTokenValid,
    login,
    register,
    getFirebaseErrorMessage,
    isAuthModalOpen,
    authModalMode,
    openAuthModal,
    closeAuthModal,
    sendVerificationEmail,
    checkEmailVerified,
    resetPassword,
  }), [
    user, token, loading, isAuthenticated, logout, forceTokenRefresh, 
    isTokenValid, login, register, getFirebaseErrorMessage, isAuthModalOpen, 
    authModalMode, openAuthModal, closeAuthModal, sendVerificationEmail, 
    checkEmailVerified, resetPassword
  ]);
  
  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};